import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { BookOpen, Brain, FileText, TrendingUp, Briefcase, Menu, X, Award, Sun, Moon, LogOut, Sparkles } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';

const Header = () => {
  const { user, logout: handleLogout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isDarkMode, toggleDarkMode } = useTheme();

  const navItems = [
    { path: '/', label: 'Home', icon: Award },
    { path: '/dsa', label: 'DSA', icon: BookOpen },
    { path: '/aptitude', label: 'Aptitude', icon: Brain },
    { path: '/resume', label: 'Resume', icon: FileText },
    { path: '/account', label: 'Dashboard', icon: TrendingUp },
    { path: '/jobs', label: 'Jobs', icon: Briefcase }
  ];

  return (
    <header className="sticky top-0 z-50 transition-all duration-500">
      {/* Glassmorphism background */}
      <div className="absolute inset-0 bg-white/70 dark:bg-[#0a0a1a]/80 backdrop-blur-2xl border-b border-gray-200/50 dark:border-gray-800/50" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="flex justify-between items-center h-16 md:h-[4.5rem]">
          {/* Logo */}
          <NavLink to="/" className="flex items-center space-x-2.5 group">
            <div className="relative">
              <div className="w-9 h-9 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/20 group-hover:shadow-indigo-500/40 transition-all duration-300 group-hover:scale-110 group-hover:rotate-3">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              {/* Animated glow ring */}
              <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 opacity-0 group-hover:opacity-30 blur-md transition-opacity duration-300" />
            </div>
            <h1 className="text-xl font-bold font-display">
              <span className="neon-text">PrepMaster</span>
            </h1>
          </NavLink>

          {/* Desktop Nav */}
          <nav className="hidden xl:flex items-center space-x-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === '/'}
                  className={({ isActive }) =>
                    `flex items-center space-x-1.5 px-3.5 py-2 rounded-xl text-[13px] font-medium transition-all duration-300 relative ${
                      isActive
                        ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg shadow-indigo-500/25'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100/80 dark:hover:bg-gray-800/60'
                    }`
                  }
                >
                  <Icon className="w-4 h-4" />
                  <span>{item.label}</span>
                </NavLink>
              );
            })}
          </nav>

          {/* Right Section */}
          <div className="hidden xl:flex items-center space-x-3">
            {/* Theme Toggle */}
            <button
              onClick={toggleDarkMode}
              className="relative w-10 h-10 rounded-xl bg-gray-100 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:text-amber-500 dark:hover:text-amber-400 hover:border-amber-300 dark:hover:border-amber-700 transition-all duration-300 hover:shadow-md overflow-hidden"
              aria-label="Toggle Dark Mode"
            >
              <div className={`absolute transform transition-all duration-500 ${isDarkMode ? 'rotate-180 scale-0 opacity-0' : 'rotate-0 scale-100 opacity-100'}`}>
                <Moon className="w-[18px] h-[18px]" />
              </div>
              <div className={`absolute transform transition-all duration-500 ${isDarkMode ? 'rotate-0 scale-100 opacity-100' : '-rotate-180 scale-0 opacity-0'}`}>
                <Sun className="w-[18px] h-[18px]" />
              </div>
            </button>

            {/* User Section */}
            {user ? (
              <div className="flex items-center space-x-3 pl-3 border-l border-gray-200 dark:border-gray-700">
                {/* User Avatar */}
                <NavLink to="/account" className="flex items-center space-x-2.5 hover:opacity-80 transition-opacity group/user">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white text-sm font-bold shadow-md group-hover/user:shadow-indigo-500/30 transition-shadow">
                    {user.name.charAt(0).toUpperCase()}
                  </div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300 max-w-[120px] truncate group-hover/user:text-indigo-500 dark:group-hover/user:text-indigo-400">
                    {user.name}
                  </span>
                </NavLink>
                <button
                  onClick={handleLogout}
                  className="w-9 h-9 rounded-lg flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-300"
                  title="Logout"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <NavLink
                to="/auth"
                className="relative px-5 py-2.5 text-sm font-semibold rounded-xl overflow-hidden group"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 transition-all duration-300 group-hover:opacity-90" />
                <div className="absolute inset-0 bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 opacity-0 group-hover:opacity-100 transition-all duration-500" />
                <span className="relative text-white flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5" />
                  Get Started
                </span>
              </NavLink>
            )}
          </div>

          {/* Mobile Controls */}
          <div className="xl:hidden flex items-center space-x-2">
            <button
              onClick={toggleDarkMode}
              className="w-9 h-9 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-gray-500 dark:text-gray-400 transition-colors"
            >
              {isDarkMode ? <Sun className="w-[18px] h-[18px]" /> : <Moon className="w-[18px] h-[18px]" />}
            </button>
            <button
              className="w-9 h-9 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-gray-600 dark:text-gray-300 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle Menu"
            >
              <div className="relative w-5 h-5">
                <X className={`w-5 h-5 absolute inset-0 transform transition-all duration-300 ${mobileMenuOpen ? 'rotate-0 opacity-100' : 'rotate-90 opacity-0'}`} />
                <Menu className={`w-5 h-5 absolute inset-0 transform transition-all duration-300 ${mobileMenuOpen ? '-rotate-90 opacity-0' : 'rotate-0 opacity-100'}`} />
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`xl:hidden overflow-hidden transition-all duration-400 ease-[cubic-bezier(0.22,1,0.36,1)] ${mobileMenuOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}`}>
          <nav className="pb-6 pt-2 space-y-1 stagger-children">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === '/'}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center space-x-3 px-4 py-3 rounded-xl w-full transition-all duration-300 ${
                      isActive
                        ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg shadow-indigo-500/20'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800/60'
                    }`
                  }
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </NavLink>
              );
            })}
            
            <div className="pt-4 mt-3 border-t border-gray-200/50 dark:border-gray-700/50">
              {user ? (
                <div className="flex items-center justify-between px-4 py-2">
                  <NavLink to="/account" onClick={() => setMobileMenuOpen(false)} className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-bold shadow-md">
                      {user.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">{user.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{user.email}</p>
                    </div>
                  </NavLink>
                  <button 
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }} 
                    className="px-4 py-2 text-sm font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  >
                    Logout
                  </button>
                </div>
              ) : (
                <NavLink 
                  to="/auth" 
                  onClick={() => setMobileMenuOpen(false)} 
                  className="block w-full text-center py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl font-semibold shadow-lg shadow-indigo-500/20"
                >
                  Get Started
                </NavLink>
              )}
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;
